import { FileTextIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "react-aria-components";
import { Controller } from "react-hook-form";

import { Badge } from "@/components/badge";
import { TiptapEditor } from "@/components/ui/TiptapEditor";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess.tsx";
import { CollapsibleBox } from "@/features/request/edit/components/CollapsibleBox";
import { OriginalInputModal } from "@/features/request/edit/components/design-brief-section/OriginalInputModal";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useIsMBC } from "@/features/request/edit/hooks/useIsMBC";
import { useTemplateLock } from "@/features/request/edit/hooks/useTemplateLock.tsx";

export function DesignBriefSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const isMBC = useIsMBC();
  const { canAccess } = useRoleAccess();
  const { control } = useEditRequestFormContext();
  const { debouncedUpdateField } = useFieldUpdate();
  const { isLocked } = useTemplateLock();

  if (!isMBC) return null;

  const isReadOnly =
    canAccess("edit-request.details.designer.view") || isLocked;

  return (
    <CollapsibleBox
      icon={<FileTextIcon className="text-white" size={16} />}
      iconClassName="bg-pink-500"
      title="Design brief"
      titleChildren={
        <div className="flex gap-xs">
          <Badge isRounded size="small" variant="brand">
            AI generated
          </Badge>
          <Button
            className="underline text-text-brand-tertiary font-semibold text-xs outline-none cursor-pointer"
            onPress={() => setModalOpen(true)}
          >
            View input
          </Button>
        </div>
      }
    >
      <Controller
        control={control}
        name="briefText"
        render={({ field: { value }, fieldState: { error } }) => (
          <TiptapEditor
            className="max-h-[500px] max-w-full @container"
            errorMessage={error?.message}
            isInvalid={!!error}
            isReadOnly={isReadOnly}
            onChangeHTML={(e) => debouncedUpdateField("briefText", e)}
            value={value}
          />
        )}
      />

      <OriginalInputModal isOpen={modalOpen} onOpenChange={setModalOpen} />
    </CollapsibleBox>
  );
}
